import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ONBOARDING_PROJECT_KEY } from '@/lib/first-run-keys';

export function OnboardingForm({
  defaultOrgName,
  defaultProjectName,
}: {
  defaultOrgName: string;
  defaultProjectName: string;
}): import('react/jsx-runtime').JSX.Element {
  const [orgName, setOrgName] = useState(defaultOrgName);
  const [projectName, setProjectName] = useState(defaultProjectName);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function onSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!orgName.trim() || !projectName.trim()) return;
    setLoading(true);
    setError(null);

    try {
      const orgRes = await fetch('/api/orgs', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: orgName.trim() }),
      });
      const orgData = (await orgRes.json()) as { id?: string; error?: string };
      if (!orgRes.ok) {
        setError(orgData.error ?? 'Failed to create organisation');
        setLoading(false);
        return;
      }
      const orgId = orgData.id;
      if (orgId == null) {
        setError('Invalid organisation response');
        setLoading(false);
        return;
      }

      const projectRes = await fetch('/api/projects', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: projectName.trim(), org_id: orgId }),
      });
      const projectData = (await projectRes.json()) as {
        id?: string;
        name?: string;
        error?: string;
      };
      if (!projectRes.ok) {
        setError(projectData.error ?? 'Failed to create project');
        setLoading(false);
        return;
      }

      const switchRes = await fetch('/api/orgs/switch', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orgId }),
      });
      if (!switchRes.ok) {
        setError('Failed to set active organisation');
        setLoading(false);
        return;
      }

      if (projectData.id != null) {
        try {
          sessionStorage.setItem(
            ONBOARDING_PROJECT_KEY,
            JSON.stringify({
              id: projectData.id,
              name: projectData.name ?? projectName.trim(),
            }),
          );
        } catch {
          // ignore storage failures
        }
      }

      navigate('/', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="flex min-h-screen items-center justify-center px-4"
      data-testid="onboarding-page"
    >
      <Card className="w-full max-w-md" data-testid="onboarding-form-card">
        <CardHeader className="text-center space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Zap className="h-5 w-5 text-[var(--flux-accent)]" />
            <span className="font-bold">fluxRun</span>
          </div>
          <CardTitle className="text-lg">Create your first app</CardTitle>
          <p className="text-sm text-muted-foreground leading-relaxed">
            You’ll see your first execution in ~2 minutes once the SDK is installed.
          </p>
        </CardHeader>
        <CardContent>
          <form data-testid="onboarding-form" onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="org-name">Workspace</Label>
              <Input
                id="org-name"
                data-testid="onboarding-workspace"
                autoComplete="organization"
                value={orgName}
                onChange={(e): void => setOrgName(e.target.value)}
                placeholder="My workspace"
              />
              <p className="text-xs text-muted-foreground">
                Groups your projects. You can rename it later.
              </p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="project-name">First app</Label>
              <Input
                id="project-name"
                data-testid="onboarding-app-name"
                autoComplete="off"
                value={projectName}
                onChange={(e): void => setProjectName(e.target.value)}
                placeholder="my-app"
              />
            </div>
            {error != null && (
              <p
                className="text-xs text-destructive rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2"
                data-testid="onboarding-error"
              >
                {error}
              </p>
            )}
            <Button
              type="submit"
              data-testid="onboarding-submit"
              disabled={!orgName.trim() || !projectName.trim() || loading}
              className="w-full"
            >
              {loading ? 'Creating...' : 'Continue'}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
